import { useEffect, useRef, useState } from "react";
import { Input } from "@/ui/input";
import { Textarea } from "@/ui/textarea";
import { useAcoes, useEstado } from "../estado.jsx";
import { cn } from "@/lib/utils";
import { ICONES, Icone, dataDigitada, dataParcial } from "../visual.jsx";
import { Gravou, Seletor } from "./comuns.jsx";
import { CAMPO } from "./pele.ts";

/**
 * Um campo da ficha — SEMPRE ABERTO, gravando sozinho.
 *
 * Três gerações neste arquivo, e cada uma morreu pelo mesmo motivo: o dado
 * estava a um clique de distância. Primeiro o rótulo era a CHAVE do banco
 * (`funcao_cliente`) com o valor em monoespaçado à direita, só leitura — corrigir
 * a admissão que a extração leu torta custava abrir outra tela. Depois veio o
 * editar-no-lugar: clicar abria o campo, Enter gravava, Esc desistia. Melhor, e
 * ainda assim um pedágio por campo — e pior, invisível: um texto cinza sobre
 * fundo transparente não anuncia que aceita digitação, então a ficha parecia
 * relatório e quem atende ia escrever na trilha.
 *
 * Agora todo campo JÁ É o controle dele: caixa com borda, do tipo certo (data
 * se digita, seleção abre a lista do escritório, texto longo
 * abre a área que cresce). Digitou, saiu do campo, gravou — e a gravação também
 * acontece sozinha um segundo depois da última tecla, para quem sai da tela sem
 * clicar fora. O recibo é o `Gravou` verde na própria linha: aviso por campo
 * corrigido soltaria uma torrada a cada tecla de quem está arrumando a ficha
 * inteira.
 *
 * O que NÃO mudou, e é o que faz isto ser seguro: o rascunho só renasce quando o
 * CASO muda. O evento do servidor chega no meio da digitação — é a regra de
 * "recarregue a trilha, nunca o rascunho", agora valendo para vinte campos.
 */

/** Quanto tempo depois da última tecla o campo grava sozinho. */
const GRAVA_DEPOIS_DE = 900;

export default function CampoDaFicha({ chave, valor, campo, primeiro, falta = false }) {
  const { listas, caso } = useEstado();
  /* O caso cujos DADOS estão na tela, e não o da URL: ao trocar de conversa a
     URL muda na hora e os dados chegam depois. Zerar pelo da URL zerava com o
     valor do cliente ANTERIOR, e quando os dados certos chegavam ninguém
     zerava de novo — a ficha mostrava um cliente no outro (Laura, 15/10). */
  const idDoCaso = caso?.registro?.id ?? null;
  const { salvarCampo } = useAcoes();

  const texto = valor == null ? "" : String(valor);
  const tipo = campo?.tipo ?? (texto.length > 90 ? "texto_longo" : "texto");
  const rotulo = campo?.rotulo ?? chave.replaceAll("_", " ");
  /* Sim/Não é uma lista de dois, gravada como texto — é o que a peça e a
     planilha leem, e o servidor recusa qualquer outra resposta. */
  const opcoes = tipo === "selecao" ? (listas?.[campo?.lista] ?? []) : tipo === "booleano" ? ["Sim", "Não"] : null;
  const longo = tipo === "texto_longo" || texto.length > 90;
  /* PELA IA: o último que mexeu neste campo foi a máquina, e o valor na tela é
     o que ela gravou. A trilha já diz isso (evento `campo`, autor
     "IA (automático)", a mais nova primeiro); desenhar é o "quem abrir amanhã
     vê que não foi gente". Some sozinho quando uma pessoa grava por cima, porque
     aí o último evento é dela. */
  const mexida = (caso?.trilha ?? []).find((e) => e.tipo === "campo" && e.texto === chave);
  const pelaIA = texto !== "" && /automático/.test(mexida?.autor ?? "") && String(mexida?.para ?? "") === texto;

  /* Data se DIGITA, e pode ser parcial ("03/2024", "2024"): o cliente nem
     sempre manda o dia, e o calendário só aceitava o dia inteiro (30/10). Na
     tela é "dd/mm/aaaa"; no banco, `AAAA-MM-DD` cortado no que se sabe. */
  const naTela = (v) => (tipo === "data" ? dataParcial(v) : v);
  const [rascunho, setRascunho] = useState(() => naTela(texto));
  const [errado, setErrado] = useState(false);
  const relogio = useRef(null);
  const ultimo = useRef(texto);

  /* Só na troca de CASO (e de campo): reler o caso não pode apagar o que está na
     mão de quem digita. */
  useEffect(() => {
    /* O que ficou digitado no caso anterior não grava em lugar nenhum: o
       relógio, se disparasse agora, levaria o texto para o caso novo. */
    clearTimeout(relogio.current);
    setRascunho(naTela(texto));
    setErrado(false);
    ultimo.current = texto;
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- de propósito */
  }, [idDoCaso, chave]);

  useEffect(() => () => clearTimeout(relogio.current), []);

  const gravar = (bruto) => {
    const novo = tipo === "data" ? dataDigitada(bruto) : String(bruto ?? "").trim();
    /* Data que não fecha fica na mão de quem digita, marcada — gravar
       mandaria o servidor recusar, e apagar perderia o que foi escrito. */
    setErrado(novo === null);
    if (novo === null || novo === ultimo.current.trim()) return;
    ultimo.current = novo;
    /* O id de QUANDO se digitou: gravar no "caso aberto" mandaria para o
       cliente que a pessoa abriu enquanto o relógio corria. */
    if (idDoCaso) salvarCampo(chave, novo, idDoCaso);
  };

  const escrever = (bruto) => {
    setRascunho(bruto);
    clearTimeout(relogio.current);
    relogio.current = setTimeout(() => gravar(bruto), GRAVA_DEPOIS_DE);
  };

  const aoSair = (e) => {
    clearTimeout(relogio.current);
    gravar(e.target.value);
  };

  /* Enter grava no campo de uma linha; no texto longo ele é quebra de linha, e
     quem grava é o relógio ou sair do campo. */
  const teclas = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      clearTimeout(relogio.current);
      setRascunho(naTela(ultimo.current));
      setErrado(false);
      e.currentTarget.blur();
      return;
    }
    if (e.key === "Enter" && tipo !== "texto_longo") {
      e.preventDefault();
      clearTimeout(relogio.current);
      gravar(e.currentTarget.value);
    }
  };

  /* A pele de campo da casa (36, raio 10, borda 3:1, foco em borda terra) em
     TODO tipo: o texto media 31 e a seleção 45, e a ficha parecia ter três
     controles diferentes. */
  /* Sem caixa até o ponteiro ou o foco chegar (29/10): com borda em cada linha
     a ficha era um formulário de 26 caixas; assim ela se LÊ como cadastro, e
     continua sendo o controle — o hover acende o fundo, o foco acende a borda
     terra. O selo IA, ao lado do rótulo, diz quem escreveu. */
  const pele = cn(
    CAMPO,
    "border-transparent bg-transparent px-2 text-foreground shadow-none placeholder:not-italic placeholder:text-tinta-fantasma hover:border-transparent hover:bg-foreground/4 focus-visible:bg-card",
  );

  /* Seleção: o próprio seletor é o campo, e escolher grava. Limpar é o ✕ ao
     lado, que só existe quando há o que limpar — o `Select` do registro recusa
     item com `value=""` (é o valor dele para "nada escolhido"). */
  if (opcoes?.length)
    return (
      <Linha rotulo={rotulo} primeiro={primeiro} chave={chave} falta={falta} pelaIA={pelaIA}>
        <span className="flex items-center gap-1">
          <Seletor
            rotulo={texto ? rotulo : `Escolher ${rotulo.toLowerCase()}`}
            comoCampo
            limpo
            vazio="Escolher…"
            largura="100%"
            valor={texto}
            aoEscolher={(v) => v !== texto && salvarCampo(chave, v)}
            opcoes={opcoes.map((o) => ({ id: o, rotulo: o }))}
          />
          {texto ? (
            <button
              type="button"
              onClick={() => salvarCampo(chave, "")}
              data-dica={`Limpar ${rotulo.toLowerCase()}`}
              aria-label={`Limpar ${rotulo.toLowerCase()}`}
              /* No dedo não há hover que o revele: lá ele fica à vista. */
              className="alvo flex size-mini flex-none cursor-pointer items-center justify-center rounded-controle border-none bg-transparent p-0 text-tinta-fraca opacity-0 transition-opacity duration-[var(--t-curta)] group-hover/linha:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 hover:text-destructive"
            >
              <Icone de={ICONES.fechar} tamanho={14} />
            </button>
          ) : null}
        </span>
      </Linha>
    );

  if (longo)
    return (
      <Linha rotulo={rotulo} primeiro={primeiro} chave={chave} falta={falta} pelaIA={pelaIA}>
        <Textarea
          value={rascunho}
          onChange={(e) => escrever(e.target.value)}
          onBlur={aoSair}
          onKeyDown={teclas}
          aria-label={rotulo}
          placeholder="Adicionar…"
          className={cn(pele, "h-auto min-h-[40px] py-1.5 leading-[1.5] max-[759px]:h-auto field-sizing-content")}
        />
      </Linha>
    );

  return (
    <Linha rotulo={rotulo} primeiro={primeiro} chave={chave} falta={falta} pelaIA={pelaIA}>
      <Input
        type={tipo === "numero" ? "number" : tipo === "telefone" ? "tel" : tipo === "email" ? "email" : "text"}
        value={rascunho}
        onChange={(e) => escrever(e.target.value)}
        onBlur={aoSair}
        onKeyDown={teclas}
        aria-label={rotulo}
        aria-invalid={errado || undefined}
        placeholder={tipo === "data" ? "dd/mm/aaaa, mm/aaaa ou aaaa" : "Adicionar…"}
        className={cn(pele, (tipo === "numero" || tipo === "data") && "tabular-nums")}
      />
    </Linha>
  );
}

/**
 * A linha: o NOME em cima, o valor embaixo na largura inteira.
 *
 * Era rótulo à esquerda e campo à direita, e numa coluna de 300px isso dava
 * 120px para "Data do primeiro contato" (que quebrava em duas linhas) e 160
 * para o valor (que cortava "Rede Doce Pão Panifica…"). Empilhado, o nome cabe
 * numa linha e o valor tem a largura toda — é como um cadastro se lê.
 */
function Linha({ rotulo, chave, children, primeiro, falta = false, pelaIA = false }) {
  return (
    <Gravou
      chave={`campo:${chave}`}
      /* A divisória é uma linha à parte, reta e recuada das pontas: a borda de
         cima seguia o raio do `Gravou` e curvava nas extremidades. */
      classe={`group/linha flex flex-col gap-0.5 py-2 ${primeiro ? "" : "before:absolute before:inset-x-2 before:top-0 before:h-px before:bg-foreground/8"}`}
    >
      {/* `falta`: a peça imprime este campo e ele está em branco — é a marca
          que o aviso do alto da ficha promete ("estão marcados abaixo"). */}
      <span
        className={`flex min-w-0 items-center gap-1.5 px-2 text-apoio leading-[1.3] ${falta ? "font-semibold text-terra" : "text-tinta-fraca"}`}
      >
        <span className="min-w-0 truncate">
          {rotulo}
          {falta ? <span className="font-medium"> · falta na peça</span> : null}
        </span>
        {pelaIA ? (
          <span
            className="flex-none rounded-[5px] bg-verde/12 px-1.5 text-selo font-bold text-verde"
            data-dica="Preenchido pela IA, a partir da conversa"
          >
            IA
          </span>
        ) : null}
      </span>
      <div className="min-w-0">{children}</div>
    </Gravou>
  );
}
